
import { Search, X } from 'lucide-react';
import { useRouter } from '@/lib/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HeroBackdropLayers, heroDescriptionClass } from '@/components/help-center/latest-power-shared';
import HeroFloatingSmilies from '@/components/help-center/HeroFloatingSmilies';
import InlineSearchDropdown from '@/components/help-center/InlineSearchDropdown';
import PowerSearchResultRow from '@/components/help-center/powers/PowerSearchResultRow';
import { useCatalogPowers } from '@/components/painel/powers/powers-cms-context';
import { cn } from '@/lib/utils';
import { filterPowersByQuery, getPowersDetailedStats } from '@/lib/powers-data';

interface PowersPageHeroProps {
  searchQuery: string;
  onSearch: (q: string) => void;
}

const HERO_STATS = [
  { iconSrc: '/icons/power.svg', label: 'Powers', valueKey: 'totalPowers' as const, color: 'text-sky-300', bg: 'bg-sky-400/10' },
  { iconSrc: '/icons/smile.svg', label: 'Smilies', valueKey: 'totalSmilies' as const, color: 'text-amber-300', bg: 'bg-amber-400/10' },
  { iconSrc: '/icons/pawn.svg', label: 'Peões', valueKey: 'totalPawns' as const, color: 'text-violet-300', bg: 'bg-violet-400/10' },
  { iconSrc: '/icons/grouppowers.svg', label: 'Group Powers', valueKey: 'totalGroups' as const, color: 'text-emerald-300', bg: 'bg-emerald-400/10' },
];

/** Pinta o SVG com a cor do texto (currentColor) via máscara CSS. */
function MaskedIcon({ src, className }: { src: string; className?: string }) {
  return (
    <span
      className={cn('inline-block bg-current', className)}
      style={{
        maskImage: `url(${src})`,
        maskRepeat: 'no-repeat',
        maskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskImage: `url(${src})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        WebkitMaskSize: 'contain',
      }}
      aria-hidden
    />
  );
}

const DROPDOWN_LIMIT = 8;

export default function PowersPageHero({
  searchQuery,
  onSearch,
}: PowersPageHeroProps) {
  const router = useRouter();
  const { powers: catalogPowers, hydrated } = useCatalogPowers();
  const containerRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const stats = useMemo(
    () => getPowersDetailedStats(hydrated ? catalogPowers : undefined),
    [hydrated, catalogPowers]
  );

  const allMatches = useMemo(
    () => (searchQuery.trim().length >= 2 ? filterPowersByQuery(catalogPowers, searchQuery) : []),
    [catalogPowers, searchQuery]
  );
  const visibleMatches = useMemo(() => allMatches.slice(0, DROPDOWN_LIMIT), [allMatches]);

  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      setShowDropdown(true);
      setActiveIndex(-1);
    } else {
      setShowDropdown(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectPower = useCallback(
    (slug: string) => {
      setShowDropdown(false);
      onSearch('');
      router.push(`/powers/${slug}`);
    },
    [onSearch, router]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visibleMatches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const power = visibleMatches[activeIndex];
      if (power) handleSelectPower(power.slug);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      inputRef.current?.blur();
    }
  };

  return (
    <section className="relative z-30 overflow-x-hidden bg-[#030023]">
      <div className="absolute inset-0 overflow-hidden" aria-hidden>
        <HeroBackdropLayers className="absolute inset-0" />
      </div>

      <HeroFloatingSmilies />

      <div className="pointer-events-none relative z-[20] mx-auto max-w-5xl px-6 pb-12 pt-44 md:pb-14 md:pt-52">
        <div className="-mb-2 flex justify-center sm:-mb-3 md:-mb-4">
          <img
            src="/icons/powerpediabook.png"
            alt="Enciclopédia de Powers"
            className="animate-powerpedia-book-float h-40 w-auto object-contain drop-shadow-[0_14px_32px_rgba(56,189,248,0.32)] sm:h-48 md:h-56"
            decoding="async"
            draggable={false}
          />
        </div>

        <div className="mb-8 text-center">
          <h1 className="mb-4 font-poppins text-5xl font-bold tracking-tight text-white md:text-7xl">
            Powerpédia
          </h1>
          <p
            className={cn(
              'mx-auto max-w-[34rem] leading-snug',
              heroDescriptionClass
            )}
          >
            Explore o catálogo de powers, smilies e peões do xat.
            <br />
            Aprenda a usar cada recurso com os nossos guias.
          </p>
        </div>

        <div className="pointer-events-auto mx-auto mb-7 max-w-2xl" ref={containerRef}>
          <div className="relative isolate z-10 rounded-2xl group">
            <div
              className={cn(
                'pointer-events-none absolute -inset-3 rounded-[1.35rem] bg-[#2563eb] blur-2xl transition-opacity duration-300',
                focused ? 'opacity-[0.45]' : 'opacity-0'
              )}
              aria-hidden
            />
            <div
              className={cn(
                'pointer-events-none absolute -inset-2 rounded-[1.2rem] bg-[#000b6a] blur-xl transition-opacity duration-300',
                focused ? 'opacity-50' : 'opacity-0'
              )}
              aria-hidden
            />
            <div
              ref={searchBarRef}
              className={cn(
                'relative flex items-center gap-4 rounded-2xl border border-blue-100/90 bg-white px-6 py-4 shadow-2xl shadow-blue-900/15 transition-[border-color,box-shadow] duration-300',
                focused &&
                  'border-[#000b6a]/45 shadow-[0_0_0_2px_rgba(0,11,106,0.12),0_0_24px_4px_rgba(0,11,106,0.35),0_0_56px_12px_rgba(0,11,106,0.22)]'
              )}
            >
              <Search
                className={cn(
                  'h-6 w-6 shrink-0 transition-colors duration-300',
                  focused ? 'text-[#000b6a]' : 'text-gray-400'
                )}
              />
              <input
                ref={inputRef}
                type="text"
                placeholder="Pesquisar power..."
                value={searchQuery}
                onChange={(e) => onSearch(e.target.value)}
                onFocus={() => {
                  setFocused(true);
                  if (searchQuery.trim().length >= 2) setShowDropdown(true);
                }}
                onBlur={() => setFocused(false)}
                onKeyDown={handleKeyDown}
                className="w-full bg-transparent font-poppins text-lg text-gray-900 placeholder-gray-400 focus:outline-none"
              />
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => {
                    onSearch('');
                    setShowDropdown(false);
                    inputRef.current?.focus();
                  }}
                  className="shrink-0 rounded-full p-1 transition-colors hover:bg-gray-100"
                  aria-label="Limpar busca"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              ) : null}
            </div>
          </div>

          <InlineSearchDropdown
            open={showDropdown}
            anchorRef={searchBarRef}
            dropdownRef={dropdownRef}
            query={searchQuery}
            totalCount={allMatches.length}
            visibleCount={visibleMatches.length}
            emptyTitle="Nenhum power encontrado"
            emptyDescription="Tente buscar por nome, slug ou categoria"
          >
            <ul>
              {visibleMatches.map((power, i) => (
                <PowerSearchResultRow
                  key={power.slug}
                  power={power}
                  query={searchQuery}
                  active={activeIndex === i}
                  onSelect={() => handleSelectPower(power.slug)}
                  onHover={() => setActiveIndex(i)}
                />
              ))}
            </ul>
          </InlineSearchDropdown>
        </div>

        <div className="pointer-events-auto mx-auto max-w-2xl">
          <div
            className={cn(
              'relative overflow-hidden rounded-2xl',
              'border border-white/[0.08] bg-gradient-to-br from-white/[0.06] to-white/[0.015]',
              'shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] backdrop-blur-sm'
            )}
          >
            <div className="relative grid grid-cols-2 sm:grid-cols-4">
              {HERO_STATS.map(({ iconSrc, label, valueKey, color, bg }, index) => (
                <div
                  key={label}
                  className={cn(
                    'relative flex flex-col items-center gap-1.5 px-3 py-4',
                    index % 2 === 1 && 'border-l border-white/[0.08]',
                    index >= 2 && 'border-t border-white/[0.08] sm:border-t-0',
                    index > 0 && 'sm:border-l sm:border-white/[0.08]'
                  )}
                >
                  {index > 0 ? (
                    <span
                      className="pointer-events-none absolute -left-px top-1/2 hidden h-14 w-px -translate-y-1/2 bg-gradient-to-b from-transparent via-white/[0.14] to-transparent sm:block"
                      aria-hidden
                    />
                  ) : null}

                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-lg ring-1 ring-white/10',
                      bg
                    )}
                  >
                    <MaskedIcon src={iconSrc} className={cn('h-5 w-5', color)} />
                  </div>
                  <span className={cn('font-poppins text-xl font-bold leading-none', color)}>
                    {stats[valueKey]}
                  </span>
                  <span className="text-center font-poppins text-[10px] font-medium uppercase tracking-wide text-sky-200/70">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
